import { t } from "i18next";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from 'moment';
import { Button, Form } from "react-bootstrap";
import { Event } from "../../../../interfaces/event";
import { getEvents } from "../../../../services/event";
import { AdminEventNew } from "./event-new";
import { AdminEventDelete } from "./event-delete";
import { AdminEventEdit } from "./event-edit";
import { Link, useNavigate } from "react-router-dom";
import { getPaginationOptions, tableStyles } from "../../../../util/data-table";

export function AdminEventList() {
    const navigate = useNavigate();

    const [events, setEvents] = useState<Event[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    const CustomName = (row: any) => (
        <span>
            <Link className="event-link" to={ '/events/' + row.row.id + '/view' }  title={ row.row.name }>
                { row.row.name }
            </Link>
        </span>
    );

    const CustomDateStart = (row: any ) => (
        <span>
            { moment(row.row.date_start).format("DD/MM/YYYY") }
        </span>
    );

    const CustomDateEnd = (row: any ) => (
        <span>
            { moment(row.row.date_end).format("DD/MM/YYYY") }
        </span>
    );
    
    const CustomChangedAt = (row: any ) => (
        <span>
            { moment(row.row.changed_at).format("DD/MM/YYYY HH:mm") }
        </span>
    );
    
    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link edit" title={ t("edit") } onClick={ handleEdit } data-id={ row.row.id }>
                <i className="bi bi-pencil-square"></i>
            </a>
            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ row.row.id }>
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

    const columns = [
        {
            id: 'id',
            name: '#',
            maxWidth: '80px',
            selector: (row: any) => row.id,
            sortable: true
        },
        {
            id: 'name',
            name: t("Name"),
            selector: (row: any) => row.name,
            sortable: true,
            cell: (row: any) => <CustomName row={row} />
        },
        {
            id: 'date_start',
            name: t('date_start'),
            maxWidth: '150px',
            selector: (row: any) => row.date_start,
            sortable: true,
            cell: (row: any) => <CustomDateStart row={row} />,
        },
        {
            id: 'date_end',
            name: t('date_end'),
            maxWidth: '150px',
            selector: (row: any) => row.date_end,
            sortable: true,
            cell: (row: any) => <CustomDateEnd row={row} />,
        },
        {
            id: 'changed_at',
            name: t('changed_at'),
            maxWidth: '200px',
            selector: (row: any) => row.changed_at,
            sortable: true,
            cell: (row: any) => <CustomChangedAt row={row} />,
        },
        {
            id: 'actions',
            name: t("actions"),
            maxWidth: '80px',
            cell: (row: any) => <CustomActions row={row} />,
        }

    ];

    useEffect(() => {
         getEvents()
            .then(response => {
                setEvents(response.data);
            })
            .catch(error => {
                localStorage.removeItem('token');
                navigate("/");
                console.log(error);
            });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const passed_events = event.target.passed_events.checked;

        getEvents(s, passed_events)
            .then(response => {
                setEvents(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: Event) => {
        const newEvents = [
            s,
            ...events
        ];
        setEvents(newEvents);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newEvents = events.filter((m: Event) => m.id != id);
        setEvents(newEvents);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedEvent: Event) => {
        setSelectedId(0);
        const newEvents = events.map((m: Event) => {
            if (m.id == changedEvent.id) {
                return changedEvent;
            } else {
                return m;
            }
        });
        setEvents(newEvents);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    return (
        <div className="events-list-container">
            <h1>
                { t("Events")}
            </h1>
            <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit } className="filters-form">
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="search" placeholder={ t("Write to search") } />
                    </Form.Group>
                    <Form.Group className="field-passed-events" controlId="passed_events">
                        <Form.Check type="checkbox" label={ t("Passed events") } name="passed_events" />
                    </Form.Group>
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                    </div>
                </Form>
            </div>
            <div className="table-container">
                <DataTable
                    columns={ columns }
                    data={events}
                    // selectableRows={ true }
                    // selectableRowsHighlight={ true }
                    responsive={ true }
                    striped={ true }
                    highlightOnHover={ true }
                    defaultSortFieldId={ 'changed_at' }
                    defaultSortAsc={ false }
                    pagination={ true }
                    customStyles={ tableStyles }
                    paginationComponentOptions={ getPaginationOptions() }
                />
            </div>            
            <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                <i className="bi bi-plus-circle"></i>
            </a>
            { showAddNew &&
                <AdminEventNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
            }
            { showDelete && (selectedId != 0) &&
                <AdminEventDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminEventEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
        </div>

    )
}